/**
 * Translation layer between the yaqatw plugin's two text-addressing schemes
 * and ProseMirror's single integer position space:
 *
 *   1. `paragraphId` + plain-text char offset within that paragraph
 *      (Highlight.attrs, currentSelectionInfo, insertHighlight*)
 *   2. 0-based body-paragraph index
 *      (getCodedParagraphIndices, getHighlightParagraphMap)
 *
 * Char offsets count text characters only (non-text inline nodes contribute
 * zero characters); ProseMirror positions additionally count node boundaries,
 * so the content of a block starting at position `pos` begins at `pos + 1`.
 *
 * Cross-paragraph ranges are NOT representable in the plugin's model (a
 * Highlight has a single paragraphId). `pmRangeToParaRange` clamps such a
 * range to the paragraph containing `from`, matching the pre-migration
 * single-paragraph limitation.
 */

import { EditorState } from "@tiptap/pm/state";
import { Node as PMNode } from "@tiptap/pm/model";

export interface BlockInfo {
  node: PMNode;
  /** Absolute position of the block node itself (content starts at pos + 1). */
  pos: number;
  /** 0-based ordinal among top-level blocks — matches Paragraph.index. */
  index: number;
}

export function findBlockByParaId(doc: PMNode, paragraphId: string): BlockInfo | null {
  let found: BlockInfo | null = null;
  doc.forEach((node, offset, index) => {
    if (!found && node.attrs.paraId === paragraphId) found = { node, pos: offset, index };
  });
  return found;
}

/** paragraphId + intra-paragraph char offset → absolute ProseMirror position. */
export function paraOffsetToPmPos(
  state: EditorState,
  paragraphId: string,
  charOffset: number
): number | null {
  const block = findBlockByParaId(state.doc, paragraphId);
  if (!block) return null;
  if (charOffset < 0 || charOffset > block.node.textContent.length) return null;

  const contentStart = block.pos + 1;
  let acc = 0;
  let result: number | null = null;
  block.node.forEach((child, offset) => {
    if (result !== null || !child.isText) return;
    const len = child.text!.length;
    if (acc + len >= charOffset) result = contentStart + offset + (charOffset - acc);
    acc += len;
  });
  // Empty paragraph (or offset 0 before any text node).
  return result ?? (charOffset === 0 ? contentStart : null);
}

/** Absolute ProseMirror position → paragraphId + char offset. */
export function pmPosToParaOffset(
  state: EditorState,
  pmPos: number
): { paragraphId: string; charOffset: number } | null {
  if (pmPos < 0 || pmPos > state.doc.content.size) return null;
  const $pos = state.doc.resolve(pmPos);
  if ($pos.depth < 1) return null; // between top-level blocks
  const block = $pos.node(1);
  const paraId = block.attrs.paraId;
  if (!paraId) return null;

  const rel = pmPos - $pos.start(1);
  let charOffset = 0;
  block.forEach((child, offset) => {
    if (offset >= rel || !child.isText) return;
    charOffset += Math.min(child.text!.length, rel - offset);
  });
  return { paragraphId: paraId, charOffset };
}

/**
 * PM range → plugin paragraph range. If the range spans paragraphs, it is
 * clamped to the paragraph containing `from` (see module docs).
 */
export function pmRangeToParaRange(
  state: EditorState,
  from: number,
  to: number
): { paragraphId: string; startOffset: number; endOffset: number } | null {
  if (to < from) [from, to] = [to, from];
  // A boundary position (e.g. AllSelection's from=0) sits between blocks;
  // nudge inward to the first block's content.
  const start =
    pmPosToParaOffset(state, from) ?? pmPosToParaOffset(state, Math.min(from + 1, to));
  if (!start) return null;
  let end = pmPosToParaOffset(state, to);
  if (!end || end.paragraphId !== start.paragraphId) {
    const block = findBlockByParaId(state.doc, start.paragraphId)!;
    end = { paragraphId: start.paragraphId, charOffset: block.node.textContent.length };
  }
  return {
    paragraphId: start.paragraphId,
    startOffset: start.charOffset,
    endOffset: end.charOffset,
  };
}

/** Plugin paragraph range → PM range. */
export function paraRangeToPmRange(
  state: EditorState,
  paragraphId: string,
  startOffset: number,
  endOffset: number
): { from: number; to: number } | null {
  const from = paraOffsetToPmPos(state, paragraphId, startOffset);
  const to = paraOffsetToPmPos(state, paragraphId, endOffset);
  if (from === null || to === null) return null;
  return { from, to };
}

/** 0-based block index → PM range of the block's CONTENT. */
export function blockIndexToPmRange(
  state: EditorState,
  index: number
): { from: number; to: number } | null {
  if (index < 0 || index >= state.doc.childCount) return null;
  let pos = 0;
  for (let i = 0; i < index; i++) pos += state.doc.child(i).nodeSize;
  return { from: pos + 1, to: pos + 1 + state.doc.child(index).content.size };
}

/**
 * Absolute PM position → 0-based index of the containing top-level block.
 * A position sitting exactly between two blocks resolves to the following one.
 */
export function pmPosToBlockIndex(state: EditorState, pmPos: number): number | null {
  if (pmPos < 0 || pmPos > state.doc.content.size) return null;
  return state.doc.resolve(pmPos).index(0);
}

export function paragraphIdToBlockIndex(state: EditorState, paragraphId: string): number | null {
  const block = findBlockByParaId(state.doc, paragraphId);
  return block ? block.index : null;
}
